/**
 * Places Directory — /places/{city_slug}/{category?}
 *
 * Auto-triggered discovery: the moment a user lands on the page we
 * fire a `POST /api/geo/places/kick/{city_slug}` (idempotent, TTL-
 * gated on the backend) so background workers refresh POIs while we
 * render. The initial paint uses whatever's already indexed; a poll
 * refreshes the list after ~20 s to pick up the fresh batch.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Hospital, GraduationCap, Church, Trees, Users, Utensils, Building2, MapPin, Globe, ImageOff, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  hospitals:  { label: "Hospitals",              Icon: Hospital,     tint: "#E11D48" },
  schools:    { label: "Schools",                Icon: GraduationCap, tint: "#0284C7" },
  colleges:   { label: "Colleges & Universities", Icon: GraduationCap, tint: "#7C3AED" },
  worship:    { label: "Places of Worship",       Icon: Church,        tint: "#B45309" },
  parks:      { label: "Parks & Nature",          Icon: Trees,         tint: "#059669" },
  charities:  { label: "Charities & Nonprofits",  Icon: Users,         tint: "#DB2777" },
  food_banks: { label: "Food Banks",              Icon: Utensils,      tint: "#EA580C" },
};

const slugToTitle = (s) => (s || "").split("/").pop().replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function PlacesDirectory() {
  const { "*": rest } = useParams();
  // URL pattern: /places/<country>/<state>/<city>[/<category>]
  const parts = (rest || "").split("/").filter(Boolean);
  const knownCats = Object.keys(CATEGORY_META);
  const maybeCat = parts[parts.length - 1];
  const category = knownCats.includes(maybeCat) ? maybeCat : null;
  const citySlug = category ? parts.slice(0, -1).join("/") : parts.join("/");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [poll, setPoll] = useState(0);

  const cityName = data?.city_name || slugToTitle(citySlug);
  const catMeta = category ? CATEGORY_META[category] : null;

  // Fire the on-visit kick + fetch initial data.
  useEffect(() => {
    if (!citySlug) return;
    let alive = true;
    // Auto-trigger backend discovery (fire-and-forget).
    axios.post(`${API}/api/geo/places/kick/${citySlug}${category ? `?category=${category}` : ""}`).catch(() => {});
    // Fetch what's indexed already.
    const url = category
      ? `${API}/api/geo/places/category/${citySlug}/${category}?limit=60`
      : `${API}/api/geo/places/directory/${citySlug}?limit=12`;
    axios.get(url)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ city_name: slugToTitle(citySlug), categories: [], count: 0, items: [] }))
      .finally(() => alive && setLoading(false));
    // Refresh once after the backend has had time to run.
    const t = setTimeout(() => alive && setPoll((n) => n + 1), 25_000);
    return () => { alive = false; clearTimeout(t); };
  }, [citySlug, category, poll]);

  if (loading) {
    return (
      <div className="my-16 text-center" data-testid="places-directory-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
        <p className="mt-3 text-sm opacity-70">Discovering places in {cityName}…</p>
      </div>
    );
  }

  return (
    <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10" data-testid="places-directory">
      <nav className="text-xs opacity-70 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/l/${citySlug}`} className="hover:underline">{cityName}</Link>
        <span className="mx-2">/</span>
        <span>Places{category ? ` · ${catMeta.label}` : ""}</span>
      </nav>

      <header className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight" data-testid="places-page-title">
          {category ? `${catMeta.label} in ${cityName}` : `Places to know in ${cityName}`}
        </h1>
        <p className="mt-2 text-sm opacity-75 max-w-2xl">
          {category
            ? `Community-verified ${catMeta.label.toLowerCase()} with locations, contacts and neighbour reviews. Local guides help keep this list fresh.`
            : `Community-verified places across ${cityName} — hospitals, schools, worship, parks, charities and more. Auto-refreshed by our discovery engine every week.`}
        </p>
      </header>

      {category ? (
        <CategoryGrid items={data?.items || []} citySlug={citySlug} category={category} />
      ) : (
        <BucketsList buckets={data?.categories || []} citySlug={citySlug} />
      )}
    </main>
  );
}

/* ─── City directory (all categories on one page) ─────────────────── */
function BucketsList({ buckets, citySlug }) {
  if (!buckets.length) {
    return (
      <EmptyState
        text="No categories indexed yet — our discovery engine is on it. Check back in ~30 seconds."
      />
    );
  }
  return (
    <div className="space-y-10" data-testid="places-buckets">
      {buckets.map((b) => {
        const meta = CATEGORY_META[b.category] || { label: b.label, Icon: Building2, tint: "#334155" };
        return (
          <section key={b.category} data-testid={`places-bucket-${b.category}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-xl sm:text-2xl inline-flex items-center gap-2">
                <meta.Icon className="w-5 h-5" style={{ color: meta.tint }} />
                {meta.label}
                <span className="text-sm font-mono opacity-60">{b.count}</span>
              </h2>
              <Link
                to={`/places/${citySlug}/${b.category}`}
                className="text-sm font-medium hover:underline whitespace-nowrap"
                style={{ color: meta.tint }}
                data-testid={`places-see-all-${b.category}`}
              >
                See all →
              </Link>
            </div>
            <CategoryGrid items={b.items.slice(0, 6)} citySlug={citySlug} category={b.category} compact />
          </section>
        );
      })}
    </div>
  );
}

/* ─── Category grid ───────────────────────────────────────────────── */
function CategoryGrid({ items, citySlug, category, compact = false }) {
  if (!items.length) {
    return <EmptyState text="Nothing indexed yet — auto-discovery is running." />;
  }
  return (
    <div className={`grid gap-4 ${compact ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
      {items.map((it) => (
        <PoiCard key={it.slug} poi={it} category={category} />
      ))}
    </div>
  );
}

/* ─── Card ────────────────────────────────────────────────────────── */
function PoiCard({ poi, category }) {
  const meta = CATEGORY_META[category] || { label: "Place", Icon: Building2, tint: "#334155" };
  const img = poi.wikipedia_image;
  return (
    <Link
      to={`/places/detail/${poi.slug}`}
      data-testid={`poi-card-${poi.slug.replaceAll('/', '__')}`}
      className="group rounded-xl border overflow-hidden transition hover:shadow-md hover:-translate-y-0.5"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
    >
      <div className="h-32 relative flex items-center justify-center overflow-hidden" style={{ background: `${meta.tint}12` }}>
        {img ? (
          <img
            src={img}
            alt={poi.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <meta.Icon className="w-10 h-10 opacity-70" style={{ color: meta.tint }} />
        )}
      </div>
      <div className="p-4">
        <div className="text-xs font-mono uppercase tracking-widest opacity-60">{meta.label}</div>
        <h3 className="mt-1 font-display text-lg leading-tight line-clamp-2">{poi.name}</h3>
        {poi.address && (
          <p className="mt-1 text-xs opacity-70 line-clamp-2 inline-flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {poi.address}
          </p>
        )}
        {poi.website && (
          <p className="mt-1 text-xs opacity-70 truncate inline-flex items-center gap-1">
            <Globe className="w-3 h-3 shrink-0" /> {poi.website.replace(/^https?:\/\//, "").split("/")[0]}
          </p>
        )}
      </div>
    </Link>
  );
}

/* ─── Empty state ─────────────────────────────────────────────────── */
function EmptyState({ text }) {
  return (
    <div className="rounded-lg border border-dashed p-8 text-sm text-center opacity-80" style={{ borderColor: "var(--wofy-border)" }}>
      <ImageOff className="w-6 h-6 mx-auto opacity-50 mb-2" />
      {text}
    </div>
  );
}
